import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// D-28 upload guardrails (browser convenience check; the server signs allowed_formats and re-checks format + size)
export const UPLOAD_ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
export const UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const UPLOAD_REJECT_MESSAGE = 'Please upload a photo (JPG, PNG, WEBP or HEIC) under 10 MB.';
export const isHeicFile = (file) => /\.(heic|heif)$/i.test(file?.name || '') || /heic|heif/i.test(file?.type || '');
export const uploadRejectionReason = (file) => {
  if (!file) return UPLOAD_REJECT_MESSAGE;
  const ext = (file.name || '').split('.').pop().toLowerCase();
  const typeOk = /^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type || '') || (!file.type && UPLOAD_ALLOWED_EXT.includes(ext));
  if (!typeOk || (file.name && !UPLOAD_ALLOWED_EXT.includes(ext))) return UPLOAD_REJECT_MESSAGE;
  if (file.size > UPLOAD_MAX_BYTES || file.size === 0) return UPLOAD_REJECT_MESSAGE;
  return null;
};

export const uploadToCloudinary = async (file, token) => {
  const rejection = uploadRejectionReason(file);
  if (rejection) throw new Error(rejection);
  try {
    const sigResponse = await axios.get(`${API_URL}/api/cloudinary/signature?resource_type=image${isHeicFile(file) ? '&heic=true' : ''}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    const { signature, timestamp, cloud_name, api_key, folder, transformation } = sigResponse.data;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', api_key);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('folder', folder);
    formData.append('backup', 'false');
    if (sigResponse.data.type) formData.append('type', sigResponse.data.type); // signed: authenticated - never publicly deliverable (D-13)
    if (transformation) formData.append('transformation', transformation); // signed: strips EXIF/GPS at ingestion
    if (sigResponse.data.allowed_formats) formData.append('allowed_formats', sigResponse.data.allowed_formats); // signed (D-28)
    if (sigResponse.data.format) formData.append('format', sigResponse.data.format); // signed: HEIC -> JPG (D-28)
    
    const uploadResponse = await fetch(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );
    
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }
    
    return await uploadResponse.json();
  } catch (error) {
    console.error('Upload failed:', error);
    throw error;
  }
};

export const createFoodEntry = async (imageUrl, publicId, notes, mealType, token, photoFormat) => {
  const response = await axios.post(
    `${API_URL}/api/food-entries`,
    { public_id: publicId, notes, meal_type: mealType, photo_format: photoFormat || null },  // D-13: the upload response URL (signed) is discarded client-side; the backend addresses photos by public_id only
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const getUserFoodEntries = async (token) => {
  const response = await axios.get(`${API_URL}/api/food-entries`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAllUsers = async (token) => {
  const response = await axios.get(`${API_URL}/api/admin/users`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};





export const getPopupNotifications = async (token) => {
  const response = await axios.get(`${API_URL}/api/notifications/popup`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const dismissPopupNotification = async (notificationId, token) => {
  const response = await axios.put(
    `${API_URL}/api/notifications/${notificationId}/dismiss-popup`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

// Analyze micronutrients for a food entry
export const analyzeMicronutrients = async (entryId, token) => {
  const response = await axios.post(`${API_URL}/api/food-entries/${entryId}/analyze-micros`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Run AI analysis on a food entry that doesn't have analysis
export const runAiAnalysis = async (entryId, token) => {
  const response = await axios.post(`${API_URL}/api/food-entries/${entryId}/run-ai-analysis`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Finalize a food entry
export const finalizeEntry = async (entryId, token) => {
  const response = await axios.post(`${API_URL}/api/food-entries/${entryId}/finalize`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Undo finalization of a food entry
export const undoFinalizeEntry = async (entryId, token) => {
  const response = await axios.post(`${API_URL}/api/food-entries/${entryId}/undo-finalize`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Update food description/analysis
export const updateFoodDescription = async (entryId, analysis, token) => {
  const response = await axios.patch(`${API_URL}/api/food-entries/${entryId}/description`, 
    { analysis },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const getAllFoodEntries = async (token) => {
  const response = await axios.get(`${API_URL}/api/admin/food-entries`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserFoodEntriesAdmin = async (userId, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/food-entries`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserBiometricsAdmin = async (userId, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/biometrics`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserDailyMacrosAdmin = async (userId, date, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/daily-macros?date=${date}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserDailyMicrosAdmin = async (userId, date, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/daily-micros?date=${date}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserWeeklySummary = async (userId, startDate, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/weekly-summary?start_date=${startDate}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUserWeeklyTrends = async (userId, weeks, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/weekly-trends?weeks=${weeks}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};








// ==================== NOTIFICATIONS API ====================

export const getNotifications = async (token) => {
  const response = await axios.get(`${API_URL}/api/notifications`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getUnreadNotificationCount = async (token) => {
  const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const markNotificationRead = async (notificationId, token) => {
  const response = await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const markAllNotificationsRead = async (token) => {
  const response = await axios.put(`${API_URL}/api/notifications/mark-all-read`, {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

// ==================== USER WEEKLY REPORT ====================

export const getUserWeeklyReport = async (startDate, token) => {
  const response = await axios.get(`${API_URL}/api/user/weekly-report?start_date=${startDate}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// ==================== HEALTH FLAGS ====================

export const getUserHealthFlags = async (token) => {
  const response = await axios.get(`${API_URL}/api/user/health-flags`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAdminUserHealthFlags = async (userId, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/health-flags`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// ==================== ADMIN USER VIEW ====================

export const getAdminUserStepsHistory = async (userId, days, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/steps/history?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const getAdminUserHeartRateHistory = async (userId, days, token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/${userId}/heart-rate/history?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};






// ==================== STEPS & HEART RATE ====================





export const getBackupStatus = async (token) => {
  const response = await axios.get(`${API_URL}/api/admin/backup/status`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const triggerManualRestore = async (token) => {
  const response = await axios.post(`${API_URL}/api/admin/backup/restore`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const syncAllToCloudinary = async (token) => {
  const response = await axios.post(`${API_URL}/api/admin/backup/sync-all`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};


// Login Help APIs
export const submitLoginHelp = async (email, description = null) => {
  const response = await axios.post(`${API_URL}/api/auth/login-help`, { email, description });
  return response.data;
};

export const getLoginHelpRequests = async (token, resolved = null) => {
  const params = resolved !== null ? `?resolved=${resolved}` : '';
  const response = await axios.get(`${API_URL}/api/admin/login-help-requests${params}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const resolveLoginHelp = async (token, requestId, notes = null) => {
  const response = await axios.put(`${API_URL}/api/admin/login-help-requests/${requestId}/resolve`, 
    { notes },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const unlockUserFromHelp = async (token, requestId) => {
  const response = await axios.post(`${API_URL}/api/admin/login-help-requests/${requestId}/unlock`, {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const deleteLoginHelp = async (token, requestId) => {
  const response = await axios.delete(`${API_URL}/api/admin/login-help-requests/${requestId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Soft-Deleted Users APIs
export const getSoftDeletedUsers = async (token) => {
  const response = await axios.get(`${API_URL}/api/admin/users/deleted`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const restoreUser = async (token, userId) => {
  const response = await axios.post(`${API_URL}/api/admin/users/${userId}/restore`, {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const purgeUser = async (token, userId) => {
  const response = await axios.delete(`${API_URL}/api/admin/users/${userId}/purge`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Q8-2: long admin actions run as background jobs with a progress row (GET /admin/tasks/{id})
export const startPurgeTask = async (token, userId) => {
  const response = await axios.post(`${API_URL}/api/admin/users/${userId}/purge/start`, {}, { headers: { Authorization: `Bearer ${token}` } });
  return response.data;
};

export const getAdminTask = async (token, taskId) => {
  const response = await axios.get(`${API_URL}/api/admin/tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
  return response.data;
};

export const waitForAdminTask = async (token, taskId, onProgress, intervalMs = 1500, maxMs = 15 * 60 * 1000) => {
  const started = Date.now();
  for (;;) {
    const t = await getAdminTask(token, taskId);
    if (onProgress) onProgress(t);
    if (t.status !== 'running') return t;
    if (Date.now() - started > maxMs) throw new Error('task still running - check Diagnostics → Background jobs');
    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

export const getPurgeReports = async (token, limit = 5) => {
  const response = await axios.get(`${API_URL}/api/admin/purge-reports?limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// Cloudinary Backup Status (read-only - backups are immutable)
export const getDeletedUserBackups = async (token) => {
  const response = await axios.get(`${API_URL}/api/admin/backup/deleted-users`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};









